/* eslint-disable */
import React, { useState, useEffect } from 'react'
import { Box, Button, FormControl, FormHelperText, Grid, Typography, Divider } from '@mui/material'
import GoogleIcon from '../../../assets/images/login-google-icon.svg'
import { toast } from 'react-toastify'
import { useMsal } from '@azure/msal-react'
import AppleSignin from 'react-apple-signin-auth'
// @ts-ignore
import ReCAPTCHA from 'react-google-recaptcha'
import { loginRequest } from '../../../config/authConfig'
import './LoginPage.scss'
import DSMLogo from '../../../assets/images/DSMLogin.png'
import PasswordInput from '../../atoms/PasswordInput'
import { DSMEnvironments, ILoginPage } from './types'
import TextInput from '../../atoms/TextInput'
import PoweredByBinariiLogo from '../../atoms/PoweredByBinariiLogo'
import useAuths from '../../../hooks/useAuths'
import config from '../../../config'
import useRecaptcha from '../../../hooks/useRecaptcha'
import CustomCaptcha from '../../atoms/CustomCaptcha'
import APPLE_LOG from '../../../assets/images/Apple_logo_black.svg'
import { getMsalErrorMessage } from '../../../context/SearchFilesContext/utils'
import getSSOBaseUrl from '../../../utils/environment'

declare global {
  interface Window {
    google: any
  }
}

interface AppleSignInResponse {
  authorization: {
    code: string
    id_token: string
    state: string
  }
  user: {
    name: {
      firstName: string
      lastName: string
    }
    email: string
  }
}

const LoginPage: React.FC<ILoginPage> = ({
  loginSubmitFunction,
  navigateToForgotPassword,
  errorMessageLogin,
}) => {
  const [usernameError, setUsernameError] = useState<boolean | null>(null)
  const [username, setUsername] = useState<string>('')
  const [passwordError, setPasswordError] = useState<boolean | null>(null)
  const [password, setPassword] = useState<string>('')
  const [loginError, setLoginError] = useState<string>('')
  const [ssoProcessing, setSSOProcessing] = useState(true)

  const { instance } = useMsal()
  const baseURL = config.REACT_APP_BASE_URL
  const { handleAuth, loading } = useAuths(`${baseURL}sso-login`)

  const [showGoogleCaptcha, setShowGoogleCaptcha] = useState<boolean | null>(null)
  const { captchaToken, handleCaptchaChange, resetCaptcha, recaptchaRef, customCaptchaRef } =
    useRecaptcha()
  const bypassUsers = new Set(
    (config.RECAPTCHA_BYPASS_USERS || '')
      .split(',')
      .map(value => value.trim().toLowerCase())
      .filter(Boolean)
  )
  const isBypassUser = bypassUsers.has(username.trim().toLowerCase())
  const isCaptchaRequired = !isBypassUser

  // Get Recaptcha key
  const getRecaptchaKey = (): string => {
    const host = window.location.hostname

    switch (host) {
      case DSMEnvironments.DEV:
      case DSMEnvironments.DEV_REMOTE:
        return process.env.REACT_APP_RECAPTCHA_SITE_KEY_DEV as string
      case DSMEnvironments.STAGING:
        return (
          (process.env.REACT_APP_RECAPTCHA_SITE_KEY_STAGING as string) ||
          (process.env.REACT_APP_RECAPTCHA_SITE_KEY_DEV as string)
        )
      case DSMEnvironments.PROD:
      case DSMEnvironments.DEMO:
        return process.env.REACT_APP_RECAPTCHA_SITE_KEY_PROD as string
      default:
        return process.env.REACT_APP_RECAPTCHA_SITE_KEY_PROD as string
    }
  }

  // Disable right-click + devtools
  useEffect(() => {
    const disableRightClick = (e: MouseEvent) => e.preventDefault()

    const disableDevTools = (e: KeyboardEvent) => {
      if (
        e.key === 'F12' ||
        e.code === 'F12' ||
        (e.ctrlKey && e.shiftKey && ['I', 'J', 'C', 'K', 'E'].includes(e.key)) ||
        (e.ctrlKey && ['U', 'E'].includes(e.key)) ||
        (e.metaKey && e.altKey && ['I', 'J', 'C', 'U', 'E', 'K'].includes(e.key))
      ) {
        e.preventDefault()
      }
    }

    document.addEventListener('contextmenu', disableRightClick)
    document.addEventListener('keydown', disableDevTools)

    return () => {
      document.removeEventListener('contextmenu', disableRightClick)
      document.removeEventListener('keydown', disableDevTools)
    }
  }, [])

  useEffect(() => {
    if (isCaptchaRequired) {
      setShowGoogleCaptcha(prev => (prev === null ? Math.random() < 0.5 : prev))
      return
    }

    setShowGoogleCaptcha(null)
    if (captchaToken) {
      resetCaptcha()
    }
  }, [isCaptchaRequired])

  // MICROSOFT REDIRECT HANDLER (Fix glitch)
  useEffect(() => {
    const processMicrosoft = async () => {
      try {
        const response = await instance.handleRedirectPromise()

        if (response?.accessToken) {
          await handleAuth({ credential: response.accessToken }, 'microsoft')
        }
      } catch (err: any) {
        const message = getMsalErrorMessage(err)
        toast.error(message)
        setLoginError(message)
      } finally {
        setSSOProcessing(false) // Important: UI shown only after MSAL is done
      }
    }

    processMicrosoft()
  }, [instance])

  const handleMicrosoftLogin = async () => {
    try {
      await instance.loginRedirect({
        ...loginRequest,
        prompt: 'select_account',
      })
    } catch (err: any) {
      const message = getMsalErrorMessage(err)
      toast.error(message)
      setLoginError(message)
    }
  }

  // Google button setup
  useEffect(() => {
    if (ssoProcessing) return // ⛔ Do NOT load Google until page is fully rendered

    const loadGoogleScript = () => {
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID!,
          callback: (response: any) => {
            handleAuth({ credential: response.credential }, 'google')
          },
        })

        window.google.accounts.id.renderButton(document.getElementById('google-signin-button')!, {
          type: 'standard',
          size: 'large',
          theme: 'outline',
          text: 'continue_with',
          shape: 'rectangular',
          logo_alignment: 'center',
          width: 370,
        })
      }
    }

    if (window.google) {
      loadGoogleScript()
    } else {
      const script = document.querySelector<HTMLScriptElement>(
        'script[src="https://accounts.google.com/gsi/client"]'
      )
      if (script) {
        script.addEventListener('load', loadGoogleScript)
        return () => script.removeEventListener('load', loadGoogleScript)
      }
    }
  }, [ssoProcessing]) // <-- KEY: only run when page fully available

  // Apple login
  const handleAppleLogin = async (response: AppleSignInResponse) => {
    try {
      await handleAuth({ credential: response.authorization.id_token }, 'apple')
    } catch {
      toast.error('Apple login failed')
    }
  }

  const onLoginFormSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault()
    setPasswordError(null)
    setLoginError('')
    setUsernameError(null)

    let hasErrors = false
    if (!username) {
      setUsernameError(true)
      hasErrors = true
    }
    if (!password) {
      setPasswordError(true)
      hasErrors = true
    }

    if (isCaptchaRequired && !captchaToken) {
      toast.error('Please complete the captcha')
      hasErrors = true
    }

    if (hasErrors) return

    try {
      await loginSubmitFunction(username, password)
      if (isCaptchaRequired) {
        resetCaptcha()
      }
    } catch (error: any) {
      if (isCaptchaRequired) {
        resetCaptcha()
      }
    }
  }

  const isButtonDisabled = () =>
    username.length === 0 || password.length === 0 || (isCaptchaRequired && !captchaToken)

  // ⛔ FIX HERE — prevent login page flash
  if (ssoProcessing || loading) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          fontSize: '20px',
        }}
      >
        Loading...
      </div>
    )
  }

  return (
    <>
      <Grid
        container
        direction="column"
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.paper',
          justifyContent: 'space-between',
        }}
      >
        <Grid
          item
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            overflowY: 'auto',
            pt: 1,
          }}
        >
          <Grid
            item
            className="login-inner-container"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                '@media (min-width:1600px)': {
                  marginTop: '30px',
                },
              }}
            >
              <Box sx={{ width: '100px', height: '34.375px' }}>
                <img src={DSMLogo} alt="dsm logo" />
              </Box>
            </Box>

            <Box className="login-box">
              {/* Microsoft */}
              <Box id="microsoft-signin-button" sx={{ fontSize: '15px' }}>
                <button
                  onClick={handleMicrosoftLogin}
                  className="social-button bg-white text-gray-700 rounded-md flex items-center justify-center p-2 w-full mb-2 border border-gray-300 cursor-pointer hover:bg-gray-100 h-[38px]"
                >
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg"
                    alt="Microsoft Icon"
                    className="mr-3 w-[16px] h-[16px]"
                  />
                  <span>Continue with Microsoft</span>
                </button>
              </Box>

              {/* Apple */}
              <Box sx={{ marginTop: '-30px' }}>
                <AppleSignin
                  authOptions={{
                    clientId: process.env.REACT_APP_APPLE_CLIENT as string,
                    scope: 'name email',
                    redirectURI: getSSOBaseUrl(),
                    state: 'state',
                    nonce: 'nonce',
                    usePopup: true,
                  }}
                  onSuccess={handleAppleLogin}
                  onError={(error: Error) => console.error(error)}
                  render={(props: any) => (
                    <Box id="apple-signin-button" sx={{ fontSize: '15px' }}>
                      <button
                        {...props}
                        className="social-button bg-white text-gray-700 rounded-md flex items-center justify-center p-2 w-full mb-2 border border-gray-300 cursor-pointer hover:bg-gray-100 h-[38px]"
                      >
                        <img
                          src={APPLE_LOG}
                          alt="Apple Icon"
                          className="mr-3 w-[16px] h-[19.688px]"
                        />
                        <span>Continue with Apple</span>
                      </button>
                    </Box>
                  )}
                  uiType="light"
                />
              </Box>

              {/* Google */}
              <Box
                className="w-full mb-2"
                sx={{ marginTop: '-30px', width: '100% !important' }}
                id="google-button-container"
              >
                <div id="google-signin-button"></div>
              </Box>

              <Divider
                sx={{
                  color: '#71717A',
                  fontFamily: 'Geist',
                  fontSize: '14px',
                  fontWeight: 400,
                  mt: -3,
                }}
              >
                or continue with
              </Divider>

              <form className="login-form" onSubmit={onLoginFormSubmit}>
                <FormControl>
                  <TextInput
                    variant="outlined"
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    label="Username"
                    type="text"
                    id="username-input"
                    fullWidth
                    autoComplete="username"
                    error={!!usernameError}
                    errorMessage="Username cannot be empty"
                    sxLabel={{ color: '#18181B' }}
                  />
                </FormControl>

                <FormControl fullWidth>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <Typography variant="label" sx={{ color: '#18181B' }}>
                      Password
                    </Typography>
                    <Button onClick={navigateToForgotPassword} sx={{ minWidth: 'unset', p: 0 }}>
                      <Typography
                        sx={{
                          color: '#71717A',
                          fontFamily: 'Geist',
                          fontSize: 14,
                          fontStyle: 'normal',
                          fontWeight: 400,
                          lineHeight: 'normal',
                          textTransform: 'none',
                        }}
                      >
                        Forgot your password?
                      </Typography>
                    </Button>
                  </Box>

                  <PasswordInput
                    password={password}
                    onPasswordChange={e => setPassword(e.target.value)}
                    variant="outlined"
                    sx={{ mt: 2 }}
                    label=""
                    fullWidth
                    autoComplete="password"
                    id="password-input"
                  />

                  {passwordError && (
                    <FormHelperText error sx={{ fontSize: 12 }}>
                      Password cannot be empty
                    </FormHelperText>
                  )}
                </FormControl>

                {errorMessageLogin && (
                  <Box
                    sx={{
                      color: ' #FF2F2F',
                      fontFamily: 'Geist',
                      fontSize: '14px',
                      fontWeight: 400,
                      lineHeight: '24px',
                      letterSpacing: '0',
                      mt: -2, // optional margin-top for spacing
                    }}
                  >
                    {errorMessageLogin}
                  </Box>
                )}

                {/* Captcha */}
                {isCaptchaRequired && (
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    {showGoogleCaptcha ? (
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={getRecaptchaKey()}
                        onChange={handleCaptchaChange}
                      />
                    ) : (
                      <CustomCaptcha ref={customCaptchaRef} onVerify={handleCaptchaChange} />
                    )}
                  </Box>
                )}

                <Button
                  size="large"
                  variant="contained"
                  color="purpleTheme"
                  sx={{
                    mt: '10px',
                    borderRadius: '.5rem',
                    justifyContent: 'center',
                    textAlign: 'center',
                    '&.Mui-disabled': {
                      backgroundColor: '#E2CDFF',
                      color: '#FFFFFF',
                      opacity: 1,
                    },
                  }}
                  type="submit"
                  disabled={isButtonDisabled()}
                >
                  Login
                </Button>

                {loginError && (
                  <Typography color="error.main" textAlign="center" sx={{ mt: 1 }}>
                    {loginError}
                  </Typography>
                )}
              </form>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
          <PoweredByBinariiLogo />
        </Box>
      </Grid>
    </>
  )
}

export default LoginPage
